import React, { useEffect, useState } from "react";
/************ Components ************/
import SocialButton from "./SocialAccountButton";
import { Toasts } from "view/components/Toasts";
/************ Utils ************/
/************ API Services ************/
import apiLibrary from "services/api";
/************ Store Utils ************/
import { RootState } from "store";
import { UserProfileState } from "store/userProfile/initialState";
import { updateSocialAccountsAction } from "store/userProfile";
/************ Assets ************/
import SocialGoogleIcon from "assets/icons/SocialIcons/SocialGoogleIcon";
/************ External Library ************/
import { loadGapiInsideDOM } from "gapi-script";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import usePermissions from "hooks/usePermissions";
import useCustomBranding from "hooks/useCustomBranding";
import { handleGoogleLogin } from "utils/googleLogin";

export const ConnectGoogle: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { detachSocialAccount, attachSocialAccount } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const { primaryColor } = useCustomBranding();
  const userProfile = useSelector<RootState, UserProfileState>(
    (state) => state?.userProfile
  );

  useEffect(() => {
    (async () => {
      await loadGapiInsideDOM();
    })();
  });

  /**
   * Handles the process of determining if a social account is connected.
   *
   * @param {string} name - The name of the social account to check.
   * @returns {string} The connection status, either "Connected" or "Disconnected".
   */
  const isConnected = (name: any) => {
    return userProfile?.socialAccounts?.find((item: any) => item === name)
      ? "Connected"
      : "Disconnected";
  };

  // Listen for message from popup once on mount
  useEffect(() => {
    async function handleMessage(event: MessageEvent) {
      // Validate origin of message
      if (event.origin !== process.env.REACT_APP_SOCIAL_LOGIN) return;

      const { accessToken } = event.data;
      if (!accessToken) return;
      setIsLoading(true);
      try {
        if (
          isConnected("google") === "Connected" &&
          detachSocialAccount.canCreateDetachSocialAccount
        ) {
          const res = await apiLibrary.Auth.detachSocialAccounts({
            channel: "google",
          });
          dispatch(updateSocialAccountsAction(res?.data?.socialAccounts));
          Toasts.success(res.message);
          return;
        }
        if (attachSocialAccount.canCreateAttachSocialAccount) {
          const res = await apiLibrary.Auth.attachSocialAccounts({
            accessToken,
            channel: "google",
          });
          dispatch(updateSocialAccountsAction(res?.data?.socialAccounts));

          Toasts.success(res.message);
        }
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message ?? error?.message;
        Toasts.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch, navigate]);

  const deAttachSocialAccounts = async () => {
    if (
      isConnected("google") === "Connected" &&
      detachSocialAccount.canCreateDetachSocialAccount
    ) {
      setIsLoading(true);
      try {
        const res = await apiLibrary.Auth.detachSocialAccounts({
          channel: "google",
        });
        dispatch(updateSocialAccountsAction(res?.data?.socialAccounts));
        Toasts.success(res.message);
      } catch (error: any) {
        console.error("Error detaching social account:", error);
        const errMsg = error?.response?.data?.message ?? error.message;
        Toasts.error(errMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };
  if (isConnected("google") === "Connected") {
    return (
      <>
        <div className="relative">
          {isLoading && (
            <div className="loader absolute justify-center items-center flex w-full">
              <TailSpin
                height="30"
                width="30"
                color={primaryColor}
                ariaLabel="tail-spin-loading"
                radius="2"
                wrapperStyle={{ marginTop: "3px" }}
                wrapperClass="tailspin-loader"
                visible={true}
              />
            </div>
          )}
          <SocialButton
            onClick={deAttachSocialAccounts}
            className={isLoading ? "opacity-[0.4]" : ""}
            itemHover={isLoading ? true : false}
            disabled={
              isLoading || !detachSocialAccount.canCreateDetachSocialAccount
                ? true
                : false
            }
            icon={SocialGoogleIcon}
            text={isConnected("google")}
          />
        </div>
      </>
    );
  } else {
    return (
      <div className="relative">
        {isLoading && (
          <div className="loader absolute justify-center items-center flex w-full">
            <TailSpin
              height="30"
              width="30"
              color={primaryColor}
              ariaLabel="tail-spin-loading"
              radius="2"
              wrapperStyle={{ marginTop: "3px" }}
              wrapperClass="tailspin-loader"
              visible={true}
            />
          </div>
        )}
        <SocialButton
          className={isLoading ? "opacity-[0.4]" : ""}
          disabled={
            isLoading ||
            (isConnected("google") === "Connected" &&
              !detachSocialAccount.canCreateDetachSocialAccount) ||
            !attachSocialAccount.canCreateAttachSocialAccount
              ? true
              : false
          }
          onClick={() => {
            handleGoogleLogin();
          }}
          itemHover={isLoading ? true : false}
          icon={SocialGoogleIcon}
          text={isConnected("google")}
        />
      </div>
    );
  }
};
